import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { injectIntl, intlShape } from "react-intl";
import { Loader, Card, CardSection } from "cxa-components";
import ClaimStepper from "./ClaimStepper";
import { getClaimAccounts, getClaimItem } from "../../actions/claimActionsPP";
import messages from "components/messagesDescription";
import ClaimSubmissionGuideline from "./widgets/ClaimSubmissionGuideline";
import AccountsExhaustedInfo from "./widgets/AccountsExhaustedInfo";
import OCRReceiptUploader from "./widgets/OCRReceiptUploader";
import ClaimRHS from "./ClaimRHS";
import withMatchMediaProps from "@hocs/with-match-media-props";

const AddClaimPagePP = props => {
  const {
    match: { params },
    query,
    intl,
  } = props;
  const data = useSelector(state => state.claimsPP);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  useEffect(() => {
    //Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsLoading(true);
    dispatch(getClaimAccounts(params.benefitPeriod, params.claimTypeId, params.selectedDelegator));
    dispatch(
      getClaimItem(
        params.benefitPeriod,
        params.claimTypeId,
        query.claimantId,
        query.claimantType,
        params.selectedDelegator
      )
    ).finally(() => {
      setIsLoading(false);
    });
  }, []);

  const defaultValues = getDefaultValues(data.claimItem);
  const initialValues = getInitialValues(params, data);

  if (data.claimItem && data.claimItem.formFields) {
    setClaimantName(initialValues, data.claimItem, data.selectedClaimant);
    setReceiptCurrency(initialValues, data.claimItem);
  }

  if (
    data.claimItem &&
    data.ocrValues &&
    data.ocrValues.status === "success" &&
    data.ocrValues.fields
  ) {
    processOCRFields(data.ocrValues.fields, data.claimItem.formFields, initialValues);
  }

  const accountsExhausted =
    data &&
    data.claimAccounts &&
    data.claimAccounts.length > 0 &&
    data.claimAccounts.every(account => account.accountsExhausted);

  const hasKeyReceiptGuideline = intl.messages.hasOwnProperty("config.receiptUploadGuide");
  return (
    <Fragment>
      <ClaimStepper index={1} />
      {!data.claimItem || isLoading ? (
        <Loader />
      ) : (
        <div className="claim-container">
          <h3>
            <span className="claim-header">
              {props.intl.formatMessage(messages.claims.addReceipt)}
            </span>
          </h3>
          <div className="claim-form-container">
            <div className="cxa-grid__item--nine">
              <Card>
                {data.receipt === null && !data.skipReceipt && !accountsExhausted && (
                  <CardSection>
                    <ClaimSubmissionGuideline
                      title={intl.formatMessage(messages.claims.claimSubmissionGuideline)}
                    >
                      {hasKeyReceiptGuideline ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: intl.messages["config.receiptUploadGuide"],
                          }}
                        />
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: data.claimItem.claimSubmissionGuideline,
                          }}
                        />
                      )}
                    </ClaimSubmissionGuideline>
                  </CardSection>
                )}
                {accountsExhausted && !data.skipReceipt ? (
                  <AccountsExhaustedInfo intl={intl} />
                ) : (
                  <OCRReceiptUploader
                    data={data}
                    params={params}
                    props={props}
                    intl={intl}
                    initialValues={initialValues}
                    defaultValues={defaultValues}
                  />
                )}
              </Card>
            </div>
            <div className="cxa-grid__item--three claim-panel-mobile-container">
              <ClaimRHS {...props} />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

AddClaimPagePP.propTypes = {
  intl: intlShape.isRequired,
  match: PropTypes.shape({
    params: PropTypes.object,
  }).isRequired,
  query: PropTypes.object,
};

export default injectIntl(
  withMatchMediaProps({
    isNarrowScreen: {
      maxWidth: 680,
    },
  })(AddClaimPagePP)
);

function getDefaultValues(claimItem) {
  return (
    claimItem &&
    claimItem.formFields &&
    claimItem.formFields.reduce((obj, field) => {
      if (field.default) {
        return {
          ...obj,
          [field.name]: field.default,
        };
      } else {
        return { ...obj };
      }
    }, {})
  );
}

function getInitialValues(params, data) {
  return {
    benefitPeriod: params.benefitPeriod,
    claimTypeId: params.claimTypeId,
    receipt: data.receipt ? [data.receipt] : [],
  };
}

function setClaimantName(initialValues, claimItem, selectedClaimant) {
  initialValues.claimItemName = claimItem.claimFormTitle;
  const claimantField = claimItem.formFields.find(x => x.name === "claimantName");
  if (claimantField) {
    initialValues.claimantName =
      selectedClaimant ||
      (claimantField.items && claimantField.items.length > 0 && claimantField.items[0].id);
  }
}

function setReceiptCurrency(initialValues, claimItem) {
  const receiptCurrencyField = claimItem.formFields.find(x => x.name === "receiptCurrency");
  if (receiptCurrencyField && receiptCurrencyField.items && receiptCurrencyField.items.length > 0) {
    const defaultCurrency =
      receiptCurrencyField.items.find(x => x.isDefault) || receiptCurrencyField.items[0];
    initialValues.receiptCurrency = defaultCurrency.value;
  }
}

function processOCRFields(ocrFields, formFields, initialValues) {
  const formFieldsMap = new Map(formFields.map(field => [field.name, field]));

  ocrFields.forEach(field => {
    processField(field, formFieldsMap, initialValues);
  });
}

function processField(field, formFieldsMap, initialValues) {
  const item = formFieldsMap.get(field.name);

  let fieldName;
  switch (field.name) {
    case "receiptAmount":
      processReceiptAmount(field, formFieldsMap, initialValues);
      break;
    case "claimantName":
      processClaimantName(field, item, initialValues);
      break;
    case "provider":
      processProvider(field, item, initialValues);
      break;
    default:
      fieldName = (item && item.name) || field.name;
      initialValues[fieldName] = field.value;
  }
}

function processReceiptAmount(field, formFieldsMap, initialValues) {
  const foreignReceiptAmount = formFieldsMap.get("foreignReceiptAmount");
  const fieldName = (foreignReceiptAmount && foreignReceiptAmount.name) || field.name;
  initialValues[fieldName] = field.value;
}

function processClaimantName(field, item, initialValues) {
  const orcClaimant = item && item.items.find(x => x.label == field.value);
  if (orcClaimant) {
    initialValues.claimantName = orcClaimant.id;
  }
}

function processProvider(field, item, initialValues) {
  if (!item) {
    return;
  }

  if (item.dataType === "Autocomplete") {
    const providerValue =
      Array.isArray(item.items) && item.items.find(x => x.value === field.value);
    const isProviderObject = typeof providerValue === "object" && providerValue !== null;
    initialValues.provider = isProviderObject ? providerValue.value : "";
  } else {
    initialValues.provider = field.value;
  }
}



// WEBPACK FOOTER //
// ./components/claim/AddClaimPagePP.js